import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject, Subscription, throwError} from 'rxjs';
import {finalize, map, filter, take} from 'rxjs/operators';
import {v4 as uuidv4} from 'uuid';

import {LOCALSTORAGE_KEY_AUTH} from '../../defines/client.const';
import {
  CONTENT_TYPE_JSON_UTF8, USER_FCM_TOKEN_API_URL,
  USER_LOGIN_API_URL,
  USER_REGISTER_API_URL
} from '../../defines/protocol';
import {PLATFORM_TYPE} from '../cordova/cordova.define';
import {GenPassword, IsEmptyString} from '../../defines/common.func';
import {ApiService} from '../api/api.service';
import {FcmService} from '../fcm/fcm.service';
import {CordovaService} from '../cordova/cordova.service';


@Injectable({
  providedIn: 'root'
})
export class UserService {
  private userid: string;
  private password: string;
  public get UserId(): string {
    return this.userid;
  }

  private awt: string;
  public get Token(): string {
    // console.log('UserService::Token = ', this.awt);
    return this.awt;
  }

  private login$: Subject<boolean> = null;
  private isLoggingin = false;
  private isRegistering = false;

  public loginSuccess$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    protected cordovaSvc: CordovaService,
    private api: ApiService,
    private fcm: FcmService,
  ) {
    this.load();
  }

  public Login(resetToken: boolean = false): Observable<boolean> {
    // console.warn('UserService::Login()');
    if (resetToken) {
      this.awt = null;
    }

    if (this.login$) {
      return this.login$.asObservable();
    }

    if (IsEmptyString(this.userid)) {
      this.login$ = new Subject<boolean>();
      this.register().pipe(
        finalize(() => this.login$ = null)
      ).subscribe(this.login$);
      return this.login$.asObservable();
    }
    else if (IsEmptyString(this.awt)) {
      this.login$ = new Subject<boolean>();
      this.login().pipe(
        finalize(() => this.login$ = null)
      ).subscribe(this.login$);
      return this.login$.asObservable();
    }
    else {
      return new Observable<boolean>(observer => {
        // console.warn('[3] loginSuccess$.next(true)');
        observer.next(true);
        observer.complete();
        this.loginSuccess$.next(true);
      });
    }
  }


  private login(): Observable<boolean> {
    // console.warn('UserService::login()');
    if (this.isLoggingin) {
      console.warn('login is already running!');
      return throwError({detail: 'login is already running'});
    }
    this.isLoggingin = true;
    const url = USER_LOGIN_API_URL;

    return this.api.Post(url,
      {
        user_id: this.userid,
        password: this.password,
      },
      null,
      CONTENT_TYPE_JSON_UTF8).pipe(
        finalize(() => this.isLoggingin = false),
        map((data: {token: string}) => {
          if ('token' in data) {
            this.awt = data.token;

            this.save();
            this.loginSuccess$.next(true);
            return true;
          }
          return false;
        })
    );
  }


  private register(): Observable<boolean> {
    // console.warn('UserService::register()');
    if (this.isRegistering) {
      console.warn('user register is already running!');
      return throwError({detail: 'user register is already running'});
    }
    this.isRegistering = true;

    const url = USER_REGISTER_API_URL;
    this.userid = uuidv4();
    this.password = GenPassword(20);


    return this.api.Post(url,
        {
        user_id: this.userid,
        password: this.password,
      },
      null,
      CONTENT_TYPE_JSON_UTF8).pipe(
        finalize(() => this.isRegistering = false),
        map((data: {token: string}) => {
            console.log('data = ', data);
            if ('token' in data) {
              this.awt = data.token;

              this.save();
              this.loginSuccess$.next(true);
              return true;
            }
            return false;
          })
        );
  }

  /**
   * 로그인이 완료된 후에 실행할 수 있는 Observable을 반환합니다.
   * 이미 로그인된 상태면 즉시 값을 방출하고, 아니면 로그인이 완료될 때까지 기다립니다.
   * @returns 로그인 완료 이후에 값을 방출하는 Observable
   */
  public whenLoggedIn(): Observable<boolean> {
    // 이미 로그인되어 있는 경우 즉시 값을 방출
    if (this.loginSuccess$.getValue()) {
      return this.loginSuccess$.pipe(
        take(1)
      );
    }

    // 로그인되지 않은 경우 다음 true 값이 방출될 때까지 기다림
    return this.loginSuccess$.pipe(
      filter(success => success),
      take(1)
    );
  }

  /**
   * FCM Token을 서버에 전송한다.
   *
   * @param fcmToken
   * @param platform
   * @param model
   * @constructor
   */
  public SendFcmToken(fcmToken: string, platform: PLATFORM_TYPE, model: string = ''): Observable<any> {
    console.log('UserService::SetFcmToken()', fcmToken, platform, model);

    return this.api.Post(USER_FCM_TOKEN_API_URL, {
      fcm_token: fcmToken,
      platform,
      model,
    }, null, CONTENT_TYPE_JSON_UTF8);
  }

  toJSON(): any {
    return {
      userid:   this.userid,
      password: this.password,
      token:    this.awt,
    };
  }

  public load(): void {
    const txtAuth = localStorage.getItem(LOCALSTORAGE_KEY_AUTH);
    const auth = txtAuth ? JSON.parse(txtAuth) : null;

    this.userid     = (auth && auth.userid) ?? null;
    this.password   = (auth && auth.password) ?? null;
    this.awt        = (auth && auth.token) ?? null;

    console.log('UserService::load()', this);
  }

  public save(): void {
    localStorage.setItem(LOCALSTORAGE_KEY_AUTH, JSON.stringify(this));
  }
}
